import React, { useState, useEffect, useCallback, useRef } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// <ComboboxInline> — combo editável estilo browser nativo, mas custom.
// ────────────────────────────────────────────────────────────────────────────
// Extraído do TarefaModal (r35) pra virar o MODELO de consulta reutilizável nos
// demais modais (Acessos, etc.). Busca server-side com debounce, navegação por
// teclado, slots renderItem/renderSelecionado e botão de limpar.
//
// Sempre alimente `buscar` com um endpoint /referencia (ROTAS_AUTENTICADAS) —
// nunca o `listar()` protegido, senão dá 403 pra quem não tem a permissão do
// cadastro (ver CLAUDE.md → padrão /referencia).
//
// Props:
//   valor            — id atualmente selecionado (string/null)
//   labelAtual       — texto do valor selecionado (fallback quando não há renderSelecionado)
//   rawAtual         — objeto bruto do selecionado (passado pra renderSelecionado)
//   buscar(termo)    — async → array de itens
//   renderItem(raw)  — JSX de cada linha do dropdown (opcional)
//   renderSelecionado(rawAtual) — JSX do valor escolhido (opcional)
//   getId(raw)       — extrai o id
//   getLabel(raw)    — extrai o texto
//   onSelecionar({id,label,raw} | null) — callback de escolha/limpeza
//   placeholder, desabilitado
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  borda: '#e8eaed', texto: '#1a1a2e', muted: '#888',
  branco: '#fff', cinzaBg: '#fafafa',
};

const inputStyle = {
  width: '100%', height: '36px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
  fontFamily: "'Manrope', sans-serif",
};

function ComboboxInline({
  valor, labelAtual, rawAtual,
  buscar, renderItem, renderSelecionado,
  getId, getLabel,
  onSelecionar, placeholder, desabilitado,
  estiloTexto,   // opcional: sobrescreve fonte/cor do texto (ex.: { color, fontWeight })
}) {
  const [termoBusca, setTermoBusca] = useState('');
  const [resultados, setResultados] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [destacado, setDestacado] = useState(-1);
  const [editando, setEditando] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const carregarLista = useCallback((termo) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setCarregando(true);
        const dados = await buscar(termo);
        setResultados(dados);
        setDestacado(dados.length > 0 ? 0 : -1);
      } catch (err) {
        console.warn('Erro combobox:', err);
        setResultados([]);
      } finally {
        setCarregando(false);
      }
    }, 200);
  }, [buscar]);

  useEffect(() => {
    if (aberto) carregarLista(termoBusca);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAberto(false);
        setEditando(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setTermoBusca(v);
    setAberto(true);
    carregarLista(v);
  };

  const handleFocus = () => {
    setEditando(true);
    setTermoBusca('');
    setAberto(true);
  };

  const escolher = (raw) => {
    onSelecionar({ id: getId(raw), label: getLabel(raw), raw });
    setAberto(false);
    setEditando(false);
    setTermoBusca('');
  };

  const limpar = () => {
    onSelecionar(null);
    setTermoBusca('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!aberto) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDestacado(d => Math.min(d + 1, resultados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDestacado(d => Math.max(d - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (destacado >= 0 && destacado < resultados.length) {
        escolher(resultados[destacado]);
      }
    } else if (e.key === 'Escape') {
      setAberto(false);
      setEditando(false);
    }
  };

  const temSelecao = !!valor;
  const mostrarInput = !temSelecao || editando;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {mostrarInput ? (
        <input
          ref={inputRef}
          type="text"
          value={editando ? termoBusca : ''}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={desabilitado}
          placeholder={placeholder || 'Clique ou digite pra buscar...'}
          style={{ ...inputStyle, ...estiloTexto }}
          autoComplete="off"
        />
      ) : (
        <div
          onClick={() => {
            if (desabilitado) return;
            setEditando(true);
            setAberto(true);
            setTermoBusca('');
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          style={{
            ...inputStyle, ...estiloTexto, height: 'auto', minHeight: '36px', padding: '6px 10px',
            display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
            cursor: desabilitado ? 'default' : 'text',
          }}
        >
          {renderSelecionado ? (
            renderSelecionado(rawAtual)
          ) : (
            <span style={{ fontSize: '13px', color: COR.texto, ...estiloTexto, flex: 1 }}>
              {labelAtual}
            </span>
          )}
          {!desabilitado && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); limpar(); }}
              title="Remover"
              style={{
                marginLeft: 'auto',
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: COR.muted, fontSize: '16px', padding: '0 4px',
              }}
            >×</button>
          )}
        </div>
      )}

      {aberto && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          marginTop: '2px', backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '5px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          maxHeight: '280px', overflowY: 'auto',
        }}>
          {carregando ? (
            <div style={{ padding: '10px 14px', fontSize: '11px', color: COR.muted }}>
              Buscando...
            </div>
          ) : resultados.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: '11px', color: COR.muted }}>
              Nenhum resultado.
            </div>
          ) : resultados.map((raw, i) => (
            <div
              key={getId(raw) + '_' + i}
              onMouseEnter={() => setDestacado(i)}
              onClick={() => escolher(raw)}
              style={{
                padding: '8px 12px', fontSize: '12px',
                cursor: 'pointer',
                backgroundColor: i === destacado ? COR.cinzaBg : COR.branco,
                borderBottom: i < resultados.length - 1 ? `1px solid ${COR.borda}` : 'none',
              }}
            >
              {renderItem ? renderItem(raw) : (
                <span style={{ color: COR.texto }}>{getLabel(raw)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ComboboxInline;
